import { BasicAddressDetails } from '../types/owl';
import { Loader } from '@googlemaps/js-api-loader';
export interface SuggestedAddress {
  label: string;
  id: string;
}

class AddressSuggesterClass {
  private _apiKey: string | null = null;

  private google: typeof google | null = null;

  private get apiKey() {
    try {
      this._apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY ?? null;
    } catch (_) {
      // process isn't defined
    }
    if (!this._apiKey) {
      throw new Error(
        'Please supply NEXT_PUBLIC_GOOGLE_API_KEY environment variable to use the Google maps api',
      );
    }
    return this._apiKey;
  }

  /** The client is lazily created to avoid throwing the above apikey error if an app
   * uses @insurgrid/ui but doesn't want to use the post grid client and provide the apikey  */
  private async load() {
    if (this.google) {
      return this.google;
    }

    const loader = new Loader({
      apiKey: this.apiKey,
      version: 'weekly',
      libraries: ['places'],
    });

    const google = await loader.load();
    this.google = google;
    return google;
  }

  async getSuggestedAddresses(rawAddress: string): Promise<SuggestedAddress[]> {
    if (rawAddress.length === 0) {
      return [];
    }

    let google = this.google;
    if (!google) {
      google = await this.load();
    }

    const autocompleteService = new google.maps.places.AutocompleteService();

    return new Promise<SuggestedAddress[]>((resolve) => {
      autocompleteService.getPlacePredictions(
        {
          input: rawAddress,
        },
        (data: any) => {
          const predictions =
            data?.map((place: any) => ({
              label: place.description,
              id: place.place_id,
            })) ?? [];

          resolve(predictions);
        },
      );
    });
  }

  async getFullAddress(id: string): Promise<BasicAddressDetails> {
    return new Promise((resolve) => {
      const placeService = new this.google!.maps.places.PlacesService(
        document.createElement('div'),
      );

      placeService.getDetails({ placeId: id }, (data: any) => {
        const findComponent = (compName: string) =>
          data?.address_components?.find((comp: any) =>
            comp.types.includes(compName),
          )?.short_name ?? '';

        const address: BasicAddressDetails = {
          city: findComponent('locality'),
          postal: findComponent('postal_code'),
          street: findComponent('street_number') + ' ' + findComponent('route'),
          street2: '',
          nickname: '',
          usStateCode: findComponent('administrative_area_level_1'),
        };

        resolve(address);
      });
    });
  }
}

export const AddressSuggesterService = new AddressSuggesterClass();
